import { DesignSourceEnum } from '@insights/insights-data-provider'
import { createFileRoute, redirect } from '@tanstack/react-router'
import z from 'zod'

import { sofaTypes } from '@sofa-platform/range-data-generator/type-object'

import { DocumentTitleWithCurrentSofa } from '@/components/document-title'
import { SceneWrapper } from '@/features/scene'
import { Sidebar } from '@/features/sidebar'
import { httpPrefetchModelData } from '@/integrations/core/utils'
import { getUserFlowBasedOnPlatform } from '@/integrations/insights/user-flow'
import { initPlanningSessionQueryOptions } from '@/query-options'
import { getPlatform } from '@/utils/get-platform'

import styles from './$rangeId.$id.module.css'

const pathParamsSchema = z.object({
  country: z.string().length(2),
  language: z.string().length(2),
  id: z.string().length(8, 'Invalid product id'),
  rangeId: z.enum([...Object.keys(sofaTypes), 'future'], 'Invalid rangeId'),
  mode: z.enum(['navigator', 'planner'], 'Invalid mode'),
})

export const Route = createFileRoute('/$country/$language/$mode/$rangeId/$id')({
  params: {
    parse: (rawParams) => pathParamsSchema.parse(rawParams),
  },
  component: SceneRoute,
  beforeLoad: ({ params }) => {
    // Adding as a safeguard to prevent users from accessing the planner mode in non-production environments.
    // This should be removed once the planner mode is fully implemented and ready for testing in non-production environments.
    if (import.meta.env.MODE === 'production' && params.mode === 'planner') {
      throw redirect({
        to: '/$country/$language/$mode',
        params: {
          country: params.country,
          language: params.language,
          mode: 'navigator',
        },
        replace: true,
      })
    }
  },
  loaderDeps: ({ search: { origin, ipexGallery } }) => ({
    origin,
    ipexGallery,
  }),
  loader: ({ params, context, preload, location, deps }) => {
    httpPrefetchModelData(params.id)

    if (!preload && !location.state.skipHistory) {
      context.sceneHistory.addOrMoveToEnd({ id: params.id, rangeId: params.rangeId })
    }

    if (!preload && location.href !== window.location.pathname) {
      const designSource = location.state.fromGallery ? DesignSourceEnum.gallery : DesignSourceEnum.spr
      void context.queryClient.fetchQuery(initPlanningSessionQueryOptions(context.insightsApi, params.id, designSource))
      const platform = getPlatform(deps)
      const { flowName, availableSteps } = getUserFlowBasedOnPlatform(platform)
      void context.insightsApi?.sendUserFlowStep(flowName, availableSteps.SCENE, 2)
    }
  },
})

function SceneRoute() {
  return (
    <>
      <DocumentTitleWithCurrentSofa />
      <div className={styles.sceneRouteContainer}>
        <SceneWrapper className={styles.scene} />
        <Sidebar className={styles.sidebar} />
      </div>
    </>
  )
}
